import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, UserCog, Calendar, Eye, Trash2, Plus, BarChart3, AlertTriangle, Clock } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import FormBeneficiaire from '../components/beneficiaires/FormBeneficiaire';
import FormAuxiliaire from '../components/users/FormAuxiliaire';
import LoadingSpinner from '../components/common/LoadingSpinner';

const GestionPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('beneficiaires');
  const [loading, setLoading] = useState(true);
  
  const [showFormBeneficiaire, setShowFormBeneficiaire] = useState(false);
  const [showFormAuxiliaire, setShowFormAuxiliaire] = useState(false);
  
  const [beneficiaires, setBeneficiaires] = useState([]);
  const [auxiliaires, setAuxiliaires] = useState([]);
  const [interventions, setInterventions] = useState([]);
  const [beneficiairesAPlanifier, setBeneficiairesAPlanifier] = useState([]);
  const [stats, setStats] = useState({
    auxiliaires: 0,
    beneficiaires: 0,
    interventions: 0
  });

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersRes, benefRes, interRes, aPlanifierRes] = await Promise.all([
        api.get('/users'),
        api.get('/beneficiaires'),
        api.get('/interventions'),
        api.get('/beneficiaires/a-planifier')
      ]);

      const auxList = usersRes.data.filter(u => u.role === 'auxiliaire');
      
      setAuxiliaires(usersRes.data);
      setBeneficiaires(benefRes.data);
      setInterventions(interRes.data);
      setBeneficiairesAPlanifier(aPlanifierRes.data.beneficiaires);
      
      setStats({
        auxiliaires: auxList.length,
        beneficiaires: benefRes.data.length,
        interventions: interRes.data.filter(i => new Date(i.date) >= new Date()).length
      });
    } catch (error) {
      console.error('Erreur chargement données:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBeneficiaire = async (id) => {
    if (!window.confirm('Voulez-vous vraiment désactiver ce bénéficiaire ?')) return;
    
    try {
      await api.delete(`/beneficiaires/${id}`);
      toast.success('Bénéficiaire désactivé');
      loadData();
    } catch (error) {
      console.error('Erreur suppression:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm('Voulez-vous vraiment désactiver cet auxiliaire ?')) return;
    
    try {
      await api.delete(`/users/${id}`);
      toast.success('Auxiliaire désactivé');
      loadData();
    } catch (error) {
      console.error('Erreur suppression:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDeleteIntervention = async (id) => {
    if (!window.confirm('Voulez-vous vraiment supprimer cette intervention ?')) return;
    
    try {
      await api.delete(`/interventions/${id}`);
      toast.success('Intervention supprimée');
      loadData();
    } catch (error) {
      console.error('Erreur suppression:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const getVigilanceColor = (niveau) => {
    const colors = {
      normal: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      attention: 'bg-orange-50 text-orange-700 border-orange-200',
      urgent: 'bg-red-50 text-red-700 border-red-200'
    };
    return colors[niveau] || colors.normal;
  };

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div className="pb-20 bg-gradient-to-br from-blue-50 via-white to-purple-50 min-h-screen">
      {/* Header moderne */}
      <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-purple-700 text-white p-6 rounded-b-[2rem] shadow-xl">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-1 drop-shadow-sm">⚙️ Gestion</h1>
            <p className="text-blue-100 text-sm font-medium">Panneau de contrôle</p>
          </div>
          <div className="bg-white/20 backdrop-blur-sm rounded-full p-3">
            <BarChart3 size={28} className="text-white" />
          </div>
        </div>
      </div>

      <div className="p-4 space-y-5">
        {/* Statistiques */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl shadow-lg p-4 text-white">
            <div className="bg-white/20 rounded-full p-2 w-fit mb-2">
              <UserCog size={24} className="text-white" />
            </div>
            <p className="text-3xl font-bold mb-1">{stats.auxiliaires}</p>
            <p className="text-sm text-blue-100 font-medium">Auxiliaires</p>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl shadow-lg p-4 text-white">
            <div className="bg-white/20 rounded-full p-2 w-fit mb-2">
              <Users size={24} className="text-white" />
            </div>
            <p className="text-3xl font-bold mb-1">{stats.beneficiaires}</p>
            <p className="text-sm text-green-100 font-medium">Bénéficiaires</p>
          </div>

          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl shadow-lg p-4 text-white">
            <div className="bg-white/20 rounded-full p-2 w-fit mb-2">
              <Calendar size={24} className="text-white" />
            </div>
            <p className="text-3xl font-bold mb-1">{stats.interventions}</p>
            <p className="text-sm text-purple-100 font-medium">À venir</p>
          </div>
        </div>

        {/* Onglets */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          {/* Sélecteur d'onglets */}
          <div className="border-b border-gray-200 p-2">
            <div className="grid grid-cols-4 gap-2">
              <button
                onClick={() => setActiveTab('beneficiaires')}
                className={`py-3 rounded-xl text-center font-bold transition-all ${
                  activeTab === 'beneficiaires'
                    ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg scale-105'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Users size={20} className="mx-auto mb-1" />
                <span className="block text-xs">Bénéficiaires</span>
              </button>

              <button
                onClick={() => setActiveTab('auxiliaires')}
                className={`py-3 rounded-xl text-center font-bold transition-all ${
                  activeTab === 'auxiliaires'
                    ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg scale-105'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <UserCog size={20} className="mx-auto mb-1" />
                <span className="block text-xs">Auxiliaires</span>
              </button>

              <button
                onClick={() => setActiveTab('interventions')}
                className={`py-3 rounded-xl text-center font-bold transition-all ${
                  activeTab === 'interventions'
                    ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-lg scale-105'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Calendar size={20} className="mx-auto mb-1" />
                <span className="block text-xs">Interventions</span>
              </button>

              <button
                onClick={() => setActiveTab('a-planifier')}
                className={`py-3 rounded-xl text-center font-bold transition-all ${
                  activeTab === 'a-planifier'
                    ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg scale-105'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <AlertTriangle size={20} className="mx-auto mb-1" />
                <span className="block text-xs">À planifier</span>
                {beneficiairesAPlanifier.length > 0 && (
                  <span className="block text-xs font-bold mt-1 bg-white/30 rounded-full px-2">
                    {beneficiairesAPlanifier.length}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Contenu des onglets */}
          <div className="p-4">
            {/* Onglet Bénéficiaires */}
            {activeTab === 'beneficiaires' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-bold text-gray-800">📋 Liste des bénéficiaires</h2>
                  <button
                    onClick={() => setShowFormBeneficiaire(true)}
                    className="flex items-center gap-2 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold px-4 py-2 rounded-xl shadow-lg transition-all active:scale-95"
                  >
                    <Plus size={20} />
                    Nouveau
                  </button>
                </div>

                <div className="space-y-3">
                  {beneficiaires.map(benef => (
                    <div key={benef._id} className="bg-gradient-to-r from-white to-blue-50 border border-gray-200 rounded-xl p-4 hover:shadow-lg transition-all">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-bold text-gray-800">{benef.nom} {benef.prenom}</h3>
                            <span className={`px-2 py-1 rounded-full text-xs font-bold border ${getVigilanceColor(benef.vigilance)}`}>
                              {benef.vigilance === 'normal' ? 'Normal' : benef.vigilance === 'attention' ? 'Attention' : 'Urgent'}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600 space-y-1">
                            {benef.telephone && <div>📞 {benef.telephone}</div>}
                            {benef.adresse && (
                              <div>📍 {typeof benef.adresse === 'string' ? benef.adresse : benef.adresse.ville || benef.adresse.rue || 'Adresse non renseignée'}</div>
                            )}
                            {benef.plagesHoraires && benef.plagesHoraires.length > 0 && (
                              <div>📅 {benef.plagesHoraires.length} plage(s) horaire(s)</div>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 ml-3">
                          <button
                            onClick={() => navigate(`/beneficiaire/${benef._id}`)}
                            className="bg-blue-50 hover:bg-blue-100 text-blue-600 p-2 rounded-lg transition-all active:scale-95"
                            title="Voir détails"
                          >
                            <Eye size={20} />
                          </button>
                          <button
                            onClick={() => handleDeleteBeneficiaire(benef._id)}
                            className="bg-red-50 hover:bg-red-100 text-red-600 p-2 rounded-lg transition-all active:scale-95"
                            title="Désactiver"
                          >
                            <Trash2 size={20} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}

                  {beneficiaires.length === 0 && (
                    <div className="text-center py-10 text-gray-500">
                      <Users size={48} className="mx-auto mb-3 text-gray-300" />
                      <p className="font-medium">Aucun bénéficiaire</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Onglet Auxiliaires */}
            {activeTab === 'auxiliaires' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-bold text-gray-800">👥 Liste des auxiliaires</h2>
                  <button
                    onClick={() => setShowFormAuxiliaire(true)}
                    className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold px-4 py-2 rounded-xl shadow-lg transition-all active:scale-95"
                  >
                    <Plus size={20} />
                    Nouveau
                  </button>
                </div>

                <div className="space-y-3">
                  {auxiliaires.map(user => (
                    <div key={user._id} className="bg-gradient-to-r from-white to-purple-50 border border-gray-200 rounded-xl p-4 hover:shadow-lg transition-all">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <div className={`w-3 h-3 rounded-full ${user.isActive ? 'bg-green-500' : 'bg-gray-400'}`} />
                            <h3 className="font-bold text-gray-800">{user.username}</h3>
                            <span className="px-2 py-1 bg-purple-50 text-purple-700 border border-purple-200 rounded-full text-xs font-bold">
                              {user.role === 'coordinateur' ? 'Coordinatrice' : 'Auxiliaire'}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600 space-y-1">
                            {user.email && <div>📧 {user.email}</div>}
                            {user.telephone && <div>📞 {user.telephone}</div>}
                          </div>
                        </div>
                        <div className="flex gap-2 ml-3">
                          <button
                            onClick={() => toast('Modification bientôt disponible')}
                            className="bg-blue-50 hover:bg-blue-100 text-blue-600 p-2 rounded-lg transition-all active:scale-95"
                            title="Modifier"
                          >
                            <Eye size={20} />
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user._id)}
                            className="bg-red-50 hover:bg-red-100 text-red-600 p-2 rounded-lg transition-all active:scale-95"
                            title="Désactiver"
                          >
                            <Trash2 size={20} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}

                  {auxiliaires.length === 0 && (
                    <div className="text-center py-10 text-gray-500">
                      <UserCog size={48} className="mx-auto mb-3 text-gray-300" />
                      <p className="font-medium">Aucun auxiliaire</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Onglet Interventions */}
            {activeTab === 'interventions' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-bold text-gray-800">📆 Prochaines interventions</h2>
                  <button
                    onClick={() => navigate('/planning')}
                    className="flex items-center gap-2 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white font-bold px-4 py-2 rounded-xl shadow-lg transition-all active:scale-95"
                  >
                    <Plus size={20} />
                    Nouvelle
                  </button>
                </div>

                <div className="space-y-3">
                  {interventions
                    .filter(i => new Date(i.date) >= new Date())
                    .sort((a, b) => new Date(a.date) - new Date(b.date))
                    .slice(0, 20)
                    .map(inter => (
                      <div key={inter._id} className="bg-gradient-to-r from-white to-indigo-50 border border-gray-200 rounded-xl p-4 hover:shadow-lg transition-all">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-bold text-gray-800">
                                {inter.beneficiaire?.nom} {inter.beneficiaire?.prenom}
                              </h3>
                              <span className="px-2 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-full text-xs font-bold">
                                {inter.type}
                              </span>
                            </div>
                            <div className="text-sm text-gray-600 space-y-1">
                              <div>👤 {inter.user?.username}</div>
                              <div>📅 {new Date(inter.date).toLocaleDateString('fr-FR')} - {inter.heureDebut} à {inter.heureFin}</div>
                              {inter.statut && (
                                <div>
                                  Statut: <span className="font-bold">{inter.statut}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2 ml-3">
                            <button
                              onClick={() => navigate('/planning')}
                              className="bg-purple-50 hover:bg-purple-100 text-purple-600 p-2 rounded-lg transition-all active:scale-95"
                              title="Voir dans planning"
                            >
                              <Eye size={20} />
                            </button>
                            <button
                              onClick={() => handleDeleteIntervention(inter._id)}
                              className="bg-red-50 hover:bg-red-100 text-red-600 p-2 rounded-lg transition-all active:scale-95"
                              title="Supprimer"
                            >
                              <Trash2 size={20} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}

                  {interventions.filter(i => new Date(i.date) >= new Date()).length === 0 && (
                    <div className="text-center py-10 text-gray-500">
                      <Calendar size={48} className="mx-auto mb-3 text-gray-300" />
                      <p className="font-medium">Aucune intervention à venir</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Onglet À Planifier */}
            {activeTab === 'a-planifier' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                    <AlertTriangle className="text-orange-500" size={24} />
                    Bénéficiaires à planifier cette semaine
                  </h2>
                  <div className="bg-gradient-to-r from-orange-50 to-red-50 border-2 border-orange-300 rounded-xl px-4 py-2">
                    <span className="text-2xl font-bold text-orange-600">{beneficiairesAPlanifier.length}</span>
                    <span className="text-sm text-gray-600 ml-2">sans intervention</span>
                  </div>
                </div>

                {beneficiairesAPlanifier.length === 0 ? (
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-2xl p-10 text-center">
                    <div className="bg-green-500 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                      <Calendar size={40} className="text-white" />
                    </div>
                    <p className="text-green-800 font-bold text-lg mb-2">✓ Tous les bénéficiaires sont planifiés !</p>
                    <p className="text-green-600 text-sm">
                      Tous les bénéficiaires avec plages horaires ont au moins une intervention cette semaine.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {beneficiairesAPlanifier.map(benef => {
                      const getUrgenceConfig = (urgence) => {
                        const configs = {
                          urgent: {
                            bg: 'bg-gradient-to-r from-red-50 to-pink-50',
                            border: 'border-red-300',
                            badge: 'bg-red-500 text-white',
                            icon: 'text-red-600'
                          },
                          attention: {
                            bg: 'bg-gradient-to-r from-orange-50 to-amber-50',
                            border: 'border-orange-300',
                            badge: 'bg-orange-500 text-white',
                            icon: 'text-orange-600'
                          },
                          normal: {
                            bg: 'bg-gradient-to-r from-blue-50 to-cyan-50',
                            border: 'border-blue-200',
                            badge: 'bg-blue-500 text-white',
                            icon: 'text-blue-600'
                          }
                        };
                        return configs[urgence] || configs.normal;
                      };

                      const config = getUrgenceConfig(benef.urgence);

                      return (
                        <div 
                          key={benef._id} 
                          className={`${config.bg} border-2 ${config.border} rounded-xl p-4 hover:shadow-lg transition-all`}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-3">
                                <h3 className="font-bold text-gray-800 text-lg">{benef.nom} {benef.prenom}</h3>
                                <span className={`px-3 py-1 rounded-full text-xs font-bold ${config.badge} shadow-md`}>
                                  {benef.urgence === 'urgent' ? '🔴 URGENT' : benef.urgence === 'attention' ? '🟠 ATTENTION' : '🔵 À planifier'}
                                </span>
                              </div>
                              
                              <div className="space-y-2 text-sm">
                                {benef.telephone && (
                                  <div className="text-gray-600">📞 {benef.telephone}</div>
                                )}
                                
                                {benef.derniereIntervention ? (
                                  <div className="flex items-center gap-2 text-gray-700">
                                    <Clock size={16} className={config.icon} />
                                    <span>
                                      Dernière intervention : <strong>{format(new Date(benef.derniereIntervention), 'd MMMM yyyy', { locale: fr })}</strong>
                                    </span>
                                    {benef.joursSansPlanification !== null && (
                                      <span className={`font-bold ${config.icon}`}>
                                        (il y a {benef.joursSansPlanification} jour{benef.joursSansPlanification > 1 ? 's' : ''})
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2 text-red-600 font-bold">
                                    <AlertTriangle size={16} />
                                    <span>⚠️ Jamais planifié</span>
                                  </div>
                                )}
                                
                                <div className="flex items-center gap-2 text-gray-700">
                                  <Calendar size={16} className="text-blue-500" />
                                  <span>
                                    {benef.nombrePlagesHoraires} plage{benef.nombrePlagesHoraires > 1 ? 's' : ''} horaire{benef.nombrePlagesHoraires > 1 ? 's' : ''}
                                  </span>
                                </div>

                                {benef.plagesHoraires && benef.plagesHoraires.length > 0 && (
                                  <div className="bg-white/50 rounded-lg p-2 mt-2">
                                    <p className="text-xs font-bold text-gray-600 mb-1">Plages horaires disponibles :</p>
                                    <div className="flex flex-wrap gap-1">
                                      {benef.plagesHoraires.slice(0, 3).map((plage, idx) => {
                                        const jours = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
                                        return (
                                          <span key={idx} className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs font-medium">
                                            {jours[plage.jourSemaine]} {plage.heureDebut}-{plage.heureFin}
                                          </span>
                                        );
                                      })}
                                      {benef.plagesHoraires.length > 3 && (
                                        <span className="text-xs text-gray-500">+{benef.plagesHoraires.length - 3} autre(s)</span>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="flex gap-2 ml-3">
                              <button
                                onClick={() => navigate(`/beneficiaire/${benef._id}`)}
                                className="bg-blue-50 hover:bg-blue-100 text-blue-600 p-2 rounded-lg transition-all active:scale-95"
                                title="Voir détails"
                              >
                                <Eye size={20} />
                              </button>
                              <button
                                onClick={() => navigate('/planning')}
                                className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold px-4 py-2 rounded-xl flex items-center gap-2 shadow-lg transition-all active:scale-95"
                              >
                                <Calendar size={20} />
                                Planifier
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {showFormBeneficiaire && (
        <FormBeneficiaire
          onClose={() => setShowFormBeneficiaire(false)}
          onSuccess={loadData}
        />
      )}

      {showFormAuxiliaire && (
        <FormAuxiliaire
          onClose={() => setShowFormAuxiliaire(false)}
          onSuccess={loadData}
        />
      )}
    </div>
  );
};

export default GestionPage;
